import React from 'react';
import ThumbIcon from 'shared/components/ui/ThumbIcon';

/**
 * One step of a verdict's path: was it right, and if not, what should it have
 * said? Typed to the node's declared output (the pinned graph):
 *   boolean  two buttons, worded from the field (Pass/Fail, Yes/No, True/False)
 *   enum     one button per option
 *   number   a slider over the declared range, with the number beside it
 *   none     free text
 *
 * Controlled: `value` is { agree: true } | { agree: false, should_have_been }
 * | null. The same control sits in the per-step popover and in the verdict
 * review, so a correction reads the same everywhere.
 */

export const boolWords = (field) => (/pass/i.test(field) ? ['Pass', 'Fail'] : /^(ok|correct|grounded|answered|safe|valid)/i.test(field) ? ['Yes', 'No'] : ['True', 'False']);

export function specOf(node) {
  return node && node.output && typeof node.output === 'object' && Object.keys(node.output).length ? node.output : null;
}

/** What a step said, in words, for one field. */
export function saidText(spec, said) {
  if (said == null) return '—';
  if (typeof said !== 'object') return String(said);
  const entries = Object.entries(said);
  if (!entries.length) return '—';
  return entries.map(([k, v]) => {
    const f = spec?.[k];
    if (f?.type === 'boolean' && typeof v === 'boolean') return entries.length > 1 ? `${k} ${boolWords(k)[v ? 0 : 1]}` : boolWords(k)[v ? 0 : 1];
    const s = typeof v === 'number' ? String(Number.isInteger(v) ? v : Number(v.toFixed(3))) : typeof v === 'object' ? JSON.stringify(v) : String(v);
    return entries.length > 1 ? `${k} ${s}` : s;
  }).join(', ');
}

/** A correction, in words: what it should have been. */
export function shouldText(spec, value) {
  if (!value) return '';
  if (value.agree) return 'Right';
  const s = value.should_have_been || {};
  if (typeof s.text === 'string' && !spec) return `“${s.text.slice(0, 60)}”`;
  return saidText(spec, s);
}

/** The opening draft of a correction: what it said, with yes/no flipped. */
export function flipped(spec, said) {
  const start = {};
  if (!spec || !said || typeof said !== 'object') return start;
  for (const [k, f] of Object.entries(spec)) start[k] = f.type === 'boolean' && typeof said[k] === 'boolean' ? !said[k] : said[k];
  return start;
}

/** Every declared field answered? */
export function complete(spec, value) {
  if (!value) return false;
  if (value.agree) return true;
  const s = value.should_have_been || {};
  if (!spec) return typeof s.text === 'string' && s.text.trim().length > 0;
  return Object.keys(spec).every((k) => s[k] !== undefined && s[k] !== null && s[k] !== '');
}

const btn = 'rounded border px-2 py-0.5 text-[11px] transition-colors';
const on = 'border-[color:var(--border-strong)] bg-surface-hover font-semibold text-[color:var(--text-primary)]';
const off = 'border-[color:var(--border-default)] text-[color:var(--text-secondary)] hover:bg-surface-hover';

function NumberField({ k, f, value, onChange }) {
  const min = Number(f.min); const max = Number(f.max);
  const range = max - min;
  // A small integer scale (1-5, 0-10) moves in whole steps; anything else (0-1) in hundredths.
  const step = f.step || (range >= 2 && range <= 10 && Number.isInteger(min) && Number.isInteger(max) ? 1 : range / 100);
  const set = value !== undefined && value !== null && value !== '';
  const pct = Math.min(100, Math.max(0, (((set ? Number(value) : min) - min) / (range || 1)) * 100));
  const track = `linear-gradient(to right, var(--trodo-primary) 0%, var(--trodo-primary) ${pct}%, var(--border-default) ${pct}%, var(--border-default) 100%)`;
  return (
    <div className="flex items-center gap-2">
      <span className="w-6 shrink-0 text-right font-mono text-[10.5px] text-[color:var(--text-muted)]">{min}</span>
      <input type="range" aria-label={k} min={min} max={max} step={step} value={set ? Number(value) : min}
        onChange={(e) => onChange(Number(e.target.value))} style={{ background: track }}
        className="trodo-range min-w-0 flex-1 cursor-pointer" />
      <span className="w-6 shrink-0 font-mono text-[10.5px] text-[color:var(--text-muted)]">{max}</span>
      <input type="number" aria-label={`${k} value`} value={set ? value : ''} min={min} max={max} step="any"
        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
        className="w-16 rounded border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] px-1 py-0.5 font-mono text-[11px] text-[color:var(--text-primary)] outline-none" />
    </div>
  );
}

export default function StepAnswer({ node, said, value, onChange, compact = false }) {
  const spec = specOf(node);
  const fields = spec ? Object.entries(spec) : [];
  const agree = value?.agree === true;
  const correcting = value != null && !agree;
  const draft = value?.should_have_been || {};
  const stop = (e) => { e.stopPropagation(); e.preventDefault(); };
  const setField = (k, v) => onChange({ agree: false, should_have_been: { ...draft, [k]: v } });
  const startCorrection = (e) => { stop(e); if (!correcting) onChange({ agree: false, should_have_been: spec ? flipped(spec, said) : {} }); };

  return (
    <div className="space-y-1.5" onClick={stop} role="group" aria-label={node?.name || 'step'}>
      <div className="flex flex-wrap items-center gap-1">
        {!compact && <span className="mr-1 text-[11px] text-[color:var(--text-muted)]">Said {saidText(spec, said)}</span>}
        <button type="button" onClick={(e) => { stop(e); onChange(agree ? null : { agree: true }); }} aria-pressed={agree}
          className={`inline-flex items-center gap-1 ${btn} ${agree ? on : off}`}>
          <ThumbIcon direction="up" filled={agree} size={11} /> Right
        </button>
        <button type="button" onClick={startCorrection} aria-pressed={correcting}
          className={`inline-flex items-center gap-1 ${btn} ${correcting ? on : off}`}>
          <ThumbIcon direction="down" filled={correcting} size={11} /> Should have been
        </button>
      </div>
      {correcting && (
        <div className="space-y-1.5 pl-1">
          {spec ? fields.map(([k, f]) => (
            <div key={k} className="space-y-1">
              {fields.length > 1 && <div className="text-[10.5px] text-[color:var(--text-muted)]">{k}</div>}
              {f.type === 'boolean' ? (
                <div className="flex gap-1">
                  {[[boolWords(k)[0], true], [boolWords(k)[1], false]].map(([label, v]) => (
                    <button key={label} type="button" onClick={(e) => { stop(e); setField(k, v); }} aria-pressed={draft[k] === v}
                      className={`${btn} flex-1 ${draft[k] === v ? on : off}`}>{label}</button>
                  ))}
                </div>
              ) : f.type === 'enum' ? (
                <div className="flex flex-wrap gap-1">
                  {(f.values || []).map((opt) => (
                    <button key={opt} type="button" onClick={(e) => { stop(e); setField(k, opt); }} aria-pressed={draft[k] === opt}
                      className={`${btn} ${draft[k] === opt ? on : off}`}>{opt}</button>
                  ))}
                </div>
              ) : (
                <NumberField k={k} f={f} value={draft[k]} onChange={(v) => setField(k, v)} />
              )}
            </div>
          )) : (
            <textarea value={draft.text || ''} onChange={(e) => onChange({ agree: false, should_have_been: { text: e.target.value } })} rows={2} aria-label="What it should have said"
              placeholder="What it should have said" className="w-full rounded border border-[color:var(--border-default)] bg-[color:var(--surface-sunken)] p-1.5 text-[11px] text-[color:var(--text-primary)] outline-none" />
          )}
        </div>
      )}
    </div>
  );
}
